/**
 * Shared catalog carousel / picker footer: title + optional price row.
 *
 * Locked to {@link CATALOG_TILE_TEXT_BAND_HEIGHT} so every tile in a carousel
 * row keeps titles and card bottoms aligned, whether or not a price line is
 * present (static free e-cards omit price but keep the band height).
 */

import { Box, Group, Text, rem } from '@mantine/core';
import type { ReactNode } from 'react';
import type { OrderPricing } from '../../types';
import { brandColors, semanticColors } from '../../config/theme';
import {
  CATALOG_TILE_TEXT_BAND_HEIGHT,
} from './catalogTileConstants';
import { PriceDisplay } from './PriceDisplay';

const DEFAULT_TEXT_BG = 'var(--mantine-color-gray-0)';

/** Compact member-savings line under the list price (flower string path). */
const MEMBER_PRICE_FONT_SIZE = rem(10);

export interface CatalogTileTextBandProps {
  name: string;
  /**
   * List / effective price label when {@link pricing} is not provided
   * (e.g. flower “from $X.XX”).
   */
  priceLabel?: string;
  /**
   * Tier-aware pricing for mailed cards / e-cards. When set, renders
   * {@link PriceDisplay} compact (list + Members pay / Free for members).
   */
  pricing?: OrderPricing;
  /**
   * Optional compact member-savings label when using {@link priceLabel}
   * without {@link pricing}. Prefer {@link pricing} for mailed cards.
   */
  memberPriceLabel?: string | null;
  /** Mantine colour name for the event-tinted band background. */
  eventColor?: string | null;
  /**
   * When false, omit the price row entirely (static free e-cards) while
   * keeping the locked band height so titles still align in the row.
   */
  showPriceRow?: boolean;
  /**
   * Custom price row (e.g. flower promo {@link FlowerPriceDisplay}).
   * When set, replaces {@link priceLabel} / {@link pricing}.
   */
  priceSlot?: ReactNode;
  /** Suppress member upsell callout (ChatGPT widget policy). */
  hideMemberCallout?: boolean;
}

/**
 * Pinned title + price footer used by {@link CatalogCompactTile} and
 * e-card {@link TemplateSelectionTile} layouts.
 */
export function CatalogTileTextBand({
  name,
  priceLabel,
  pricing,
  memberPriceLabel,
  eventColor,
  showPriceRow = true,
  priceSlot,
  hideMemberCallout = false,
}: CatalogTileTextBandProps) {
  const hasPriceContent =
    showPriceRow
    && (priceSlot != null || pricing != null || (priceLabel != null && priceLabel !== ''));

  return (
    <Box
      px={rem(8)}
      py={rem(6)}
      data-catalog-tile-text
      data-tile-text-band
      style={{
        flexGrow: 0,
        flexShrink: 0,
        flexBasis: CATALOG_TILE_TEXT_BAND_HEIGHT,
        boxSizing: 'border-box',
        height: CATALOG_TILE_TEXT_BAND_HEIGHT,
        minHeight: CATALOG_TILE_TEXT_BAND_HEIGHT,
        maxHeight: CATALOG_TILE_TEXT_BAND_HEIGHT,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: rem(4),
        // Only tint when a colour is supplied — e-cards without an event colour
        // keep the neutral gray band; mailed/flower callers pass a default.
        backgroundColor: eventColor
          ? `var(--mantine-color-${eventColor}-0)`
          : DEFAULT_TEXT_BG,
        borderTop: '1px solid var(--mantine-color-gray-2)',
      }}
    >
      <Box style={{ flex: '1 1 0', minHeight: 0, overflow: 'hidden' }}>
        <Text fw={600} size="sm" lineClamp={2}>
          {name}
        </Text>
      </Box>
      {hasPriceContent ? (
        <Box data-tile-price-row style={{ flex: '0 0 auto', width: '100%' }}>
          {priceSlot != null ? (
            priceSlot
          ) : pricing ? (
            <PriceDisplay pricing={pricing} compact size="xs" hideMemberCallout={hideMemberCallout} />
          ) : (
            <Group
              gap={4}
              wrap="nowrap"
              justify="space-between"
              align="baseline"
              style={{ width: '100%' }}
            >
              <Text size="xs" c={semanticColors.text.secondary} style={{ flexShrink: 0 }}>
                {priceLabel}
              </Text>
              {memberPriceLabel ? (
                <Text
                  fz={MEMBER_PRICE_FONT_SIZE}
                  fw={600}
                  c={brandColors.success}
                  ta="right"
                  lineClamp={1}
                  aria-label={memberPriceLabel}
                  style={{ flex: '1 1 auto', minWidth: 0 }}
                >
                  {memberPriceLabel}
                </Text>
              ) : null}
            </Group>
          )}
        </Box>
      ) : null}
    </Box>
  );
}
